import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { hasChanges } from "@insite/client-framework/Store/Pages/AccountSettings/AccountSettingsSelectors";
import setInitialValues from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SetInitialValues";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ConfirmEmailChangeModal from "@insite/content-library/Components/ConfirmEmailChangeModal";
import { AccountSettingsPageContext } from "@insite/content-library/Pages/AccountSettingsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { changeCustomerSelectCustomerContainerStyles as buttonStyle } from "@tKgpco/Overrides/Widgets/ChangeCustomer/ChangeCustomerSelectCustomerContainer";
import { signInExistingAccountStyles } from "@tKgpco/Overrides/Widgets/SignIn/SignInExistingAccount";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import saveCurrentAccount from "@tKgpco/Store/Data/AccountSettings/Handlers/saveCurrentAccount";
import updateOrderConfirmationEmail from "@tKgpco/Store/Data/AccountSettings/Handlers/updateOrderConfirmationEmail";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    account: state.pages.accountSettings.editingAccount,
    isEmailValid: !state.pages.accountSettings.emailErrorMessage,
    hasChanges: hasChanges(state),
    useDefaultCustomer: state.pages.accountSettings.useDefaultCustomer,
    initialUseDefaultCustomer: state.pages.accountSettings.initialUseDefaultCustomer,
    initialEmail: state.pages.accountSettings.initialEmail,
    pageTitle: getCurrentPage(state).fields.title,
    isCheckedForOrderConfirmationEmail: state.KGPState.orderConfirmationEmail?.isCheckedForOrderConfirmationEmail,
});

const mapDispatchToProps = {
    saveCurrentAccount,
    setInitialValues,
    updateOrderConfirmationEmail,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AccountSettingsHeaderStyles {
    title: TypographyProps;
    titleWrapper: GridItemProps;
    buttonSetHidden?: HiddenProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    saveClickable?: ClickablePresentationProps;
    cancelClickable?: ClickablePresentationProps;
    buttonSet?: GridItemProps;
}

export const headerStyles: AccountSettingsHeaderStyles = {
    saveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    cancelButton: {
        // variant: "tertiary",
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            margin-right: 10px;
            text-transform: uppercase;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
        `,
    },
    buttonSetHidden: {
        below: "md",
        css: css`
            display: flex;
        `,
    },
    menuHidden: {
        above: "sm",
    },
    buttonSet: {
        css: css`
            justify-content: flex-end;
        `,
        // width: [2, 3, 4, 3, 3],
        width: [12, 12, 4, 12, 12],
    },
    titleWrapper: {
        width: [10, 9, 8, 9, 9],
    },
    title: {
        variant: "h1",
    },
    overflowMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    justify-content: flex-start;
                    width: 30px;
                    min-width: auto;
                }
            `,
        },
    },
};

const styles = headerStyles;

const AccountSettingsHeader = ({
    account,
    isEmailValid,
    hasChanges,
    pageTitle,
    useDefaultCustomer,
    initialEmail,
    isCheckedForOrderConfirmationEmail,
    saveCurrentAccount,
    setInitialValues,
    updateOrderConfirmationEmail,
}: Props) => {
    if (!account) {
        return null;
    }
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const updateSettingsHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        saveCurrentAccount({
            isCheckedForOrderConfirmationEmail,
        });
    };

    const resetInitialValues = () => {
        setInitialValues();
        updateOrderConfirmationEmail({
            isCheckedForOrderConfirmationEmail: account.properties.isUserOptedToGetOrderConfirmationEmail,
        });
    };

    const hasCheckedChanged = isCheckedForOrderConfirmationEmail
        ? account.properties.isUserOptedToGetOrderConfirmationEmail !== isCheckedForOrderConfirmationEmail
        : false;

    const invalidCustomerState = useDefaultCustomer && !account.defaultCustomerId;
    const enableSaveButton =
        (hasChanges && isEmailValid && !invalidCustomerState) || (isEmailValid && hasCheckedChanged);

    return (
        <GridContainer>
            {/* <GridItem {...styles.titleWrapper}>
                <Typography {...styles.title}>{pageTitle}</Typography>
            </GridItem> */}
            <GridItem {...styles.buttonSet}>
                <Hidden {...styles.buttonSetHidden}>
                    {(hasChanges || hasCheckedChanged) && (
                        <Button
                            // {...styles.cancelButton}
                            {...buttonStyle.cancelButton}
                            onClick={resetInitialValues}
                        >
                            {translate("Cancel")}
                        </Button>
                    )}
                    <Button
                        // {...signInExistingAccountStyles.signInButton}
                        {...buttonStyle.continueButton}
                        onClick={updateSettingsHandler}
                        disabled={!enableSaveButton}
                        data-test-selector="accountSettings_save"
                    >
                        {translate("Save")}
                    </Button>
                </Hidden>
                <Hidden {...styles.menuHidden}>
                    <OverflowMenu position="end" {...styles.overflowMenu}>
                        <Clickable
                            {...styles.cancelClickable}
                            onClick={resetInitialValues}
                            disabled={!(hasChanges || hasCheckedChanged)}
                        >
                            {translate("Cancel")}
                        </Clickable>
                        <Clickable
                            {...styles.saveClickable}
                            onClick={updateSettingsHandler}
                            disabled={!enableSaveButton}
                        >
                            {translate("Save")}
                        </Clickable>
                    </OverflowMenu>
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsHeader),
    definition: {
        group: "Account Settings",
        allowedContexts: ["AccountSettingsPage"],
        displayName: "Header",
    },
};

export default widgetModule;
